import { unwrapResult } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import forwardarrow from "../../../../assets/images/arrow_forwardb.svg";
import Exclamation from "../../../../assets/images/Exclamation.svg";
import CustomCheckbox from "../../../../components/common/customCheckbox/customCheckbox";
import Input from "../../../../components/common/Input/Input";
import TitleDescription from "../../../../components/newMotorPolicy/titleDescription/titleDescription";
import PromoCode from "../../../../components/newMotorPolicy/veiwSummaryPopup/promoCode";
import Summary from "../../../../components/newMotorPolicy/veiwSummaryPopup/summary";
import { PROMO_CODE } from "../../../../components/newMotorPolicy/veiwSummaryPopup/viewSummaryPopup";
import useMotorForm from "../../../../hooks/useMotorFormData";
import {
  getStartPolicyTransferResponse,
  getStartPolicyTransferLoading,
  startPolicyTransfer,
} from "../../../../redux/features/endorsements/transferPolicySlice";
import { policyPayTransferFees } from "../../../../redux/features/payment/policyPaySlice";
import { policyView } from "../../../../redux/features/policyDetailsSlice";
import { selectProfileData, selectProfilePolicyTransferStatus } from "../../../../redux/features/profileSlice";
import { getClientSource } from "../../../../utils/getClientSource";
import RenewMotorLayout from "../../renewalMotorPolicy/renewMotorLayout";
import ReviewMotorTable from "../../renewalMotorPolicy/reviewAndConfirmPage/reviewMotorTable";

const ConfirmTransferDetails = () => {
  const dispatch = useDispatch();

  const [searchParams] = useSearchParams();

  const [formState, setFormState] = useState({});
  const [isChecked, setIsChecked] = useState(false);
  const { t } = useTranslation();

  const oldName = searchParams.get("oldName");
  const decodedOldName = decodeURIComponent(oldName);

  const policyNumber = searchParams.get("policy");
  const decodedPolicyNumber = decodeURIComponent(policyNumber);

  const userInfo = useSelector(selectProfileData);
  const [policyInfo, setPolicyInfo] = useState("");
  const [isRefetchingFees, setIsRefetchingFees] = useState(false);

  const transferFeesResponse = useSelector(getStartPolicyTransferResponse);
  const isTransferLoading = useSelector(getStartPolicyTransferLoading);
  const transferStatus = useSelector(selectProfilePolicyTransferStatus);

  // Check if transfer fees data is valid
  const hasValidPricingData = transferFeesResponse &&
    (transferFeesResponse.Fees !== undefined || transferFeesResponse.PremiumToPay !== undefined);

  const response = {
    old_owner: decodedOldName,
    new_owner: `${userInfo?.FirstName || "-"} ${userInfo?.LastName || "-"}`,
  };

  const { saveMotorDataInRedux, removeMotorFormData } = useMotorForm(
    PROMO_CODE,
    setFormState
  );

  const applyPromoCode = () => {
    saveMotorDataInRedux(formState);
  };
  const navigate = useNavigate();

  useEffect(() => {
    // Define an async function inside the useEffect
    const fetchPolicyDetails = async () => {
      const payload = {
        PolicyNumber: decodedPolicyNumber,
        Channel: getClientSource(),
      };

      try {
        // Calling API /policy/view
        const policyViewAction = await dispatch(policyView(payload));
        const policyViewResult = unwrapResult(policyViewAction);

        setPolicyInfo(policyViewResult);
      } catch (error) {
        // Handle errors in the catch block
        toast.error("Error in getting policy details: " + error.message);
        navigate("/");
      }
    };

    // Call the async function
    fetchPolicyDetails();
  }, [dispatch, decodedPolicyNumber, navigate]);

  // Refetch transfer fees if they're missing (handles race condition from canceled requests)
  useEffect(() => {
    const refetchTransferFees = async () => {
      if (!hasValidPricingData && !isTransferLoading && !isRefetchingFees && userInfo?.Code) {
        console.log("Transfer fees missing, refetching...");
        setIsRefetchingFees(true);

        try {
          const payload = {
            PolicyNumber: decodedPolicyNumber,
            PartyCode: userInfo.Code,
          };

          const result = await dispatch(startPolicyTransfer(payload));
          const unwrappedResult = unwrapResult(result);

          if (!unwrappedResult || unwrappedResult.canceled) {
            console.log("Refetch was canceled or failed");
          } else {
            console.log("Transfer fees refetched successfully:", unwrappedResult);
          }
        } catch (error) {
          console.error("Error refetching transfer fees:", error);
          toast.error("Error loading transfer fees. Please try again.");
        } finally {
          setIsRefetchingFees(false);
        }
      }
    };

    refetchTransferFees();
  }, [hasValidPricingData, isTransferLoading, isRefetchingFees, userInfo?.Code, decodedPolicyNumber, dispatch]);

  /* Next/Payment */
  const handlePayment = async () => {
    // Validate pricing data before proceeding
    if (!hasValidPricingData) {
      toast.error("Transfer fees not available. Please try again.");
      return;
    }

    const fees = transferFeesResponse.Fees || 0;
    const premiumToPay = transferFeesResponse.PremiumToPay || 0;
    const totalPremium = (fees + premiumToPay) * (1 + 0.1);

    console.log("handlePayment - Fees:", fees, "PremiumToPay:", premiumToPay, "Total:", totalPremium);

    const payload = {
      PolicyNumber: policyNumber,
      Premium: totalPremium,
      Type: "transfer-policy",
      Channel: getClientSource(),
    };

    try {
      // Calling API policy/pay
      const linkAction = await dispatch(policyPayTransferFees(payload));
      const link = unwrapResult(linkAction);
      // console.log("Link is: ", link)
      if (link) {
        window.location.href = link;
      }
    } catch (error) {
      // alert("Error in getting payment link: ", error)
      toast.error("Error in getting payment link: ", error);
      navigate("/");
    }
  };

  return (
    <RenewMotorLayout
      backClickHandler={() => navigate("/")}
      is_bottom_sticky_layout={false}
    >
      <div className="flex flex-col items-center mx-auto max-w-lg mt-20 max767:mt-20 max767:pb-16">
        <div className="flex justify-center items-center w-full">
          <div className="p-0 w-full max767:p-4 max767:pb">
            <TitleDescription
              title={"Confirm Transfer Details"}
              description={
                "Please review the policy information carefully before proceeding."
              }
            />

            <div className="mt-10">
              <div className="bg-[#F6F8F9] mb-6 p-4 rounded-2xl">
                <h4 className="text-[#48535B] text-base not-italic font-semibold leading-6 tracking-[-0.16px] mb-4">
                  New policy ownership
                </h4>

                <div className="plan flex align-middle items-center w-full confirmtransfer">
                  <Input value={response.old_owner} disabled />
                </div>
                <div className="line h-6 border-l-2 ml-8"></div>
                <div className="plan flex align-middle gap-5 mb-5 items-center confirmtransfer">
                  <Input value={response.new_owner} disabled />
                </div>
                <div className="reviwetable">
                  <ReviewMotorTable passedFrom={"transferPolicy"} />
                </div>
              </div>{" "}
              <div className="bg-[#F6F8F9] rounded-[26px] p-4 mb-4 w-full summary-box">
                <PromoCode
                  promoCode={formState.promoCode}
                  setPromoCode={(code) =>
                    setFormState((prevState) => ({
                      ...prevState,
                      promoCode: code,
                    }))
                  }
                  applyPromoCode={applyPromoCode}
                  clearPromoCode={() => {
                    setFormState({});
                    removeMotorFormData({});
                  }}
                />

                <Summary
                  premiumText={{ en: "Transfer Fees", ar: "ar Transfer Fees" }}
                  passedFrom={"transferPolicy"}
                />
              </div>
              <div className="w-full max767:px-4 max767:py-0">
                {/* Warning Message */}
                <div className="alertBox yellow flex items-start gap-4 self-stretch border bg-[#FDE9D2] p-4 rounded-[10px] border-solid border-[#FBC98E]">
                  <img src={Exclamation} alt="danger img" />
                  <span className="text-[#252C32] text-sm not-italic font-semibold leading-6 tracking-[-0.084px]">
                    {t(
                      "motorInsurance.newMotorInsuranceFlow.paymentSummary.disclaimer"
                    )}
                  </span>
                </div>

                <div className="flex">
                  <CustomCheckbox
                    Label={
                      <div className="flex">
                        <label
                          htmlFor="agreement"
                          className="text-[#252C32] text-sm not-italic font-normal leading-6"
                        >
                          {t(
                            "motorInsurance.newMotorInsuranceFlow.paymentSummary.agreement"
                          )}
                          <a
                            href="#"
                            className="text-[#00384D] text-sm not-italic font-semibold leading-6 underline"
                          >
                            {t(
                              "motorInsurance.newMotorInsuranceFlow.paymentSummary.policy"
                            )}
                          </a>{" "}
                          {t(
                            "motorInsurance.newMotorInsuranceFlow.paymentSummary.insurance"
                          )}
                        </label>
                      </div>
                    }
                    isChecked={isChecked}
                    setIsChecked={setIsChecked}
                  />
                </div>

                <button
                  className={`w-full text-[#48535B] text-lg not-italic font-semibold leading-6 tracking-[-0.18px] flex justify-center p-3 rounded-[10px] ${
                    isChecked && hasValidPricingData && !isTransferLoading && !isRefetchingFees
                      ? "bg-[#55C6EF] rounded-[10px] cursor-pointer"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                  disabled={!isChecked || !hasValidPricingData || isTransferLoading || isRefetchingFees}
                  onClick={handlePayment}
                >
                  {t(
                    "motorInsurance.newMotorInsuranceFlow.paymentSummary.proceed_payment"
                  )}{" "}
                  <img
                    src={forwardarrow}
                    className=" w-6 h-6 ml-[5px] rtl:rotate-180 rtl:mr-2"
                    alt="forward arrow"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </RenewMotorLayout>
  );
};

export default ConfirmTransferDetails;
